import React from 'react';
import { LazySuspense } from 'react-loosely-lazy';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import { Loading } from '@atlassian/help-center-common-component/loading';
import type { LoadingPosition } from '@atlassian/help-center-common-component/loading';
import type { LooselyLoadableLoadingComponentProps, Options } from './types';
import { islooselyLazyJsErrorsToSentryEnabled } from 'feature-flags';

interface LoadingComponentProps {
    error?: Error;
    timeout?: number;
    loadingPosition?: LoadingPosition;
    LoadingSkeleton?: React.ComponentType<LooselyLoadableLoadingComponentProps>;
}

interface ErrorBoundaryProps {
    onError?: (error: Error) => void;
    renderError: (error: Error) => JSX.Element;
}
class ErrorBoundary extends React.PureComponent<ErrorBoundaryProps, { error: Error | undefined }> {
    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    state = {
        error: undefined,
    };

    componentDidCatch(error: Error): void {
        if (islooselyLazyJsErrorsToSentryEnabled()) {
            // Differentiate between a chunkLoadError and other JS error.
            if (error.name === 'ChunkLoadError') {
                trackError('async.loader.failed', {}, error);
                this.props.onError?.(error);
            } else {
                this.props.onError?.(error);
                // this error will be caught by ScreenErrorBoundary at route level which will add it to sentry with packageName derived from route ScreenName
                throw error;
            }
        } else {
            trackError('async.loader.failed', {}, error);
            this.props.onError?.(error);
        }
    }

    render() {
        const error = this.state.error;

        return error ? this.props.renderError(error) : this.props.children;
    }
}

//Disabling eslint rule as display name is necessary for the component for better debugging

// eslint-disable-next-line prefer-arrow-callback
const LoadingComponent: React.FC<LoadingComponentProps> = React.memo(function LoadingComponent({
    error,
    LoadingSkeleton,
    timeout,
    loadingPosition,
}) {
    const [isTimedOut, setIsTimedOut] = React.useState(false);
    React.useEffect(() => {
        let timer: NodeJS.Timeout;
        if (timeout) {
            timer = setTimeout(() => {
                setIsTimedOut(true);
            }, timeout);
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [timeout]);

    if (LoadingSkeleton) {
        return <LoadingSkeleton isLoading={error ? false : true} error={error} loadingPosition={loadingPosition} />;
    }

    return (
        <Loading
            isLoading={error ? false : true}
            error={error}
            loadingPosition={loadingPosition}
            timedOut={isTimedOut}
        />
    );
});

const LooselyLoadableWrapper = <TProps extends object>(options: Options<TProps>) => {
    const Component = options.loader;

    const LazyComponent: React.FC<TProps> & { preload: typeof Component.preload } = (props) => {
        const renderError = React.useCallback((error: Error) => {
            return <LoadingComponent error={error} LoadingSkeleton={options.LoadingSkeleton} />;
        }, []);

        return (
            <ErrorBoundary renderError={renderError}>
                <LazySuspense
                    fallback={<LoadingComponent LoadingSkeleton={options.LoadingSkeleton} timeout={options.timeout} />}
                >
                    <Component {...props} />
                </LazySuspense>
            </ErrorBoundary>
        );
    };

    LazyComponent.preload = Component.preload;

    return LazyComponent;
};

export default LooselyLoadableWrapper;
